import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import PayoutRequestSection from './PayoutRequestSection';
import { 
  DollarSign, TrendingUp, Wallet, CreditCard, ArrowUpRight, ArrowDownLeft,
  Clock, CheckCircle, AlertCircle, Loader2, Banknote, Receipt, PiggyBank,
  ChevronRight, Calendar, Star, Briefcase, Download, Filter, RefreshCw,
  Zap, Percent, Info, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const transactionTypeConfig = {
  earning: { label: 'Job Earning', color: 'bg-green-100 text-green-800', icon: DollarSign },
  early_payout: { label: 'Same Day Pay', color: 'bg-blue-100 text-blue-800', icon: Zap },
  tip: { label: 'Tip', color: 'bg-purple-100 text-purple-800', icon: Star },
  early_payout_fee: { label: 'Fee Received', color: 'bg-yellow-100 text-yellow-800', icon: Percent },
  commission: { label: 'Commission', color: 'bg-indigo-100 text-indigo-800', icon: Briefcase },
  standard_payout: { label: 'Weekly Payout', color: 'bg-teal-100 text-teal-800', icon: Banknote }
};

// Same Day Pay Modal Component
function SameDayPayModal({ isOpen, onClose, balance, onSuccess }) {
  const { getAuthHeaders } = useAuth();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(false);

  const available = balance?.available || 0;
  const minPayout = balance?.early_payout?.min_payout || 10;
  const feeRate = 0.05;

  const requestedAmount = parseFloat(amount) || 0;
  const feeAmount = requestedAmount * feeRate;
  const youReceive = requestedAmount * 0.95;

  const canSubmit = requestedAmount >= minPayout && requestedAmount <= available;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    
    if (!confirmation) {
      setConfirmation(true);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/payouts/request-early`, {
        amount: requestedAmount,
        payout_method: 'instant'
      }, { headers: getAuthHeaders(), withCredentials: true });

      toast.success(`Same Day Pay processed! You received $${youReceive.toFixed(2)}`);
      onSuccess(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payout');
    } finally {
      setLoading(false);
      setConfirmation(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setConfirmation(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="same-day-pay-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Same Day Pay
          </DialogTitle>
          <DialogDescription>
            Get 95% of your earnings instantly. 5% fee applies.
          </DialogDescription>
        </DialogHeader>

        {!confirmation ? (
          <div className="space-y-6 py-4">
            {/* Available Balance */}
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
              <p className="text-sm text-green-700 mb-1">Available Balance</p>
              <p className="text-3xl font-bold text-green-800">${available.toFixed(2)}</p>
            </div>

            {/* Amount Input */}
            <div className="space-y-2">
              <Label htmlFor="payout-amount">Amount to Withdraw</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="payout-amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-7 text-lg"
                  placeholder="0.00"
                  min={minPayout}
                  max={available}
                  step="0.01"
                  data-testid="payout-amount-input"
                />
              </div>
              <p className="text-xs text-gray-500">Minimum: ${minPayout.toFixed(2)}</p>
            </div>

            {/* Quick Amount Buttons */}
            <div className="flex gap-2 flex-wrap">
              {[25, 50, 100].filter(v => v <= available).map(value => (
                <Button
                  key={value}
                  variant="outline"
                  size="sm"
                  onClick={() => setAmount(value.toString())}
                  className="flex-1"
                >
                  ${value}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAmount(available.toFixed(2))}
                className="flex-1 bg-green-50 border-green-300 text-green-700"
              >
                Max (${available.toFixed(2)})
              </Button>
            </div>

            {/* Breakdown */}
            {requestedAmount > 0 && (
              <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Requested Amount</span>
                  <span className="font-medium">${requestedAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">5% Early Access Fee</span>
                  <span className="text-red-600">-${feeAmount.toFixed(2)}</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between font-semibold">
                  <span>You Receive</span>
                  <span className="text-green-600">${youReceive.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-800">Confirm Same Day Pay</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    You're withdrawing <strong>${requestedAmount.toFixed(2)}</strong> with a 5% fee of <strong>${feeAmount.toFixed(2)}</strong>.
                    You will receive <strong>${youReceive.toFixed(2)}</strong> instantly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!canSubmit || loading}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
            data-testid="confirm-payout-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : confirmation ? (
              <CheckCircle className="w-4 h-4 mr-2" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            {confirmation ? 'Confirm Withdrawal' : 'Request Same Day Pay'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Transaction Row Component
function TransactionRow({ transaction }) {
  const config = transactionTypeConfig[transaction.transaction_type] || transactionTypeConfig.earning;
  const Icon = config.icon;
  const isPositive = transaction.amount > 0;
  
  return (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors" data-testid={`transaction-${transaction.transaction_id}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPositive ? 'bg-green-100' : 'bg-red-100'}`}>
          {isPositive ? (
            <ArrowDownLeft className="w-5 h-5 text-green-600" />
          ) : (
            <ArrowUpRight className="w-5 h-5 text-red-600" />
          )}
        </div>
        <div>
          <p className="font-medium text-gray-900">{transaction.description || config.label}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar className="w-3 h-3" />
            {new Date(transaction.created_at).toLocaleDateString()}
            <span className="mx-1">•</span>
            {new Date(transaction.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
      <div className="text-right">
        <p className={`font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {isPositive ? '+' : ''}{transaction.amount?.toFixed(2) || '0.00'}
        </p>
        {transaction.fee > 0 && (
          <p className="text-xs text-gray-500">Fee: ${transaction.fee.toFixed(2)}</p>
        )}
        <Badge className={`${config.color} text-xs mt-1`}>{config.label}</Badge>
      </div>
    </div>
  );
}

// Main Enhanced Earnings Component
export default function EnhancedEarningsTab({ stats = {}, recentPayments = [], isOwner = false }) {
  const { getAuthHeaders } = useAuth();
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSameDayPay, setShowSameDayPay] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balanceRes, transactionsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/payouts/balance`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/payouts/transactions`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => ({ data: { transactions: [] } })),
        axios.get(`${API}/payouts/history`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => ({ data: { payouts: [] } }))
      ]);
      
      setBalance(balanceRes.data);
      setTransactions(transactionsRes.data?.transactions || []);
      setPayoutHistory(payoutsRes.data?.payouts || []);
    } catch (error) {
      console.error('Failed to fetch earnings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handlePayoutSuccess = (data) => {
    fetchData();
  };

  const availableBalance = balance?.balance?.available || 0;
  const pendingBalance = balance?.balance?.pending || 0;
  const totalEarned = balance?.balance?.total_earned || stats.total_earnings || 0;
  const totalWithdrawn = balance?.balance?.total_withdrawn || 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enhanced-earnings-tab">
      {/* Balance Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Available Balance - Main Card */}
        <Card className="md:col-span-2 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Available Balance</p>
                <p className="text-4xl font-bold mt-1">${availableBalance.toFixed(2)}</p>
                {pendingBalance > 0 && (
                  <p className="text-green-200 text-sm mt-2">
                    + ${pendingBalance.toFixed(2)} pending
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <Button 
                  onClick={() => setShowSameDayPay(true)}
                  className="bg-white text-green-700 hover:bg-green-50 font-semibold"
                  disabled={availableBalance < 10}
                  data-testid="same-day-pay-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Same Day Pay
                </Button>
                <p className="text-xs text-green-200">Get 95% instantly</p>
              </div>
            </div>
            
            {/* Quick Stats Row */}
            <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-green-400/30">
              <div>
                <p className="text-green-200 text-xs">Total Earned</p>
                <p className="font-bold text-lg">${totalEarned.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-green-200 text-xs">Withdrawn</p>
                <p className="font-bold text-lg">${totalWithdrawn.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-green-200 text-xs">Jobs Done</p>
                <p className="font-bold text-lg">{stats.total_jobs || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Card */}
        <div className="space-y-4">
          <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500 rounded-xl flex items-center justify-center">
                  <Star className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-yellow-700">Rating</p>
                  <p className="text-xl font-bold text-yellow-800">{(stats.rating || 5.0).toFixed(1)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-purple-700">Avg Per Job</p>
                  <p className="text-xl font-bold text-purple-800">
                    ${stats.total_jobs > 0 ? ((stats.total_earnings || 0) / stats.total_jobs).toFixed(2) : '0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tabs for Transactions */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="transactions" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Transactions</span>
            </TabsTrigger>
            <TabsTrigger value="payouts" className="flex items-center gap-2">
              <Banknote className="w-4 h-4" />
              <span className="hidden sm:inline">Payouts</span>
            </TabsTrigger>
            <TabsTrigger value="request-payout" className="flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              <span className="hidden sm:inline">Request</span>
            </TabsTrigger>
          </TabsList>
          
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Earnings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Recent Earnings
                  </span>
                  <Badge className="bg-green-100 text-green-800">
                    {recentPayments.length} jobs
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-80 overflow-y-auto">
                {recentPayments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <PiggyBank className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No earnings yet</p>
                  </div>
                ) : (
                  recentPayments.map((payment, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{payment.service_name || 'Service'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(payment.completed_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="font-bold text-green-600">
                        +${(payment.provider_pay || 0).toFixed(2)}
                      </span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Payment Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-blue-600" />
                  Payment Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Commission Rate</span>
                  <span className="font-semibold text-gray-900">30% of Job Total</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Standard Payout</span>
                  <span className="font-semibold text-gray-900">Weekly (Fridays)</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <span className="text-yellow-700">Same Day Pay Fee</span>
                  <span className="font-semibold text-yellow-800">5%</span>
                </div>
                
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <p className="font-semibold mb-1">Same Day Pay</p>
                      <p>Access 95% of your available earnings instantly. A 5% fee applies and goes towards platform operations.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span>Transaction History</span>
                <Badge variant="outline">{transactions.length} transactions</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 max-h-96 overflow-y-auto">
              {transactions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">No transactions yet</p>
                  <p className="text-sm">Your earnings and withdrawals will appear here</p>
                </div>
              ) : (
                transactions.map((tx) => (
                  <TransactionRow key={tx.transaction_id} transaction={tx} />
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payouts Tab */}
        <TabsContent value="payouts">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span>Payout History</span>
                <Badge variant="outline">{payoutHistory.length} payouts</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 max-h-96 overflow-y-auto">
              {payoutHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Banknote className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">No payouts yet</p>
                  <p className="text-sm">Your Same Day Pay requests will appear here</p>
                </div>
              ) : (
                payoutHistory.map((payout) => (
                  <div 
                    key={payout.payout_id} 
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-100"
                    data-testid={`payout-${payout.payout_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        payout.status === 'completed' ? 'bg-green-100' : 
                        payout.status === 'processing' ? 'bg-yellow-100' : 'bg-gray-100'
                      }`}>
                        {payout.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : payout.status === 'processing' ? (
                          <Clock className="w-5 h-5 text-yellow-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Same Day Pay</p>
                        <p className="text-xs text-gray-500">
                          {new Date(payout.requested_at).toLocaleDateString()} at {new Date(payout.requested_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">${payout.payout_amount?.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">Fee: ${payout.fee_amount?.toFixed(2)}</p>
                      <Badge className={`text-xs mt-1 ${
                        payout.status === 'completed' ? 'bg-green-100 text-green-800' :
                        payout.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {payout.status}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Request Payout Tab */}
        <TabsContent value="request-payout">
          <PayoutRequestSection availableBalance={balance?.available || 0} />
        </TabsContent>
      </Tabs>

      {/* Same Day Pay Modal */}
      <SameDayPayModal 
        isOpen={showSameDayPay}
        onClose={() => setShowSameDayPay(false)}
        balance={balance}
        onSuccess={handlePayoutSuccess}
      />
    </div>
  );
}
